/* ----------------------------------------------------------------------------
   <hdt-card-offer> - the pay-online price strip under the product price.

   Recomputes the discounted price when the shopper picks another variant.
   It follows the same contract as the theme's own <hdt-price> element
   (assets/global.min.js): listen for "variant:change" on the product form
   named by the `form` attribute, then replace the .hdt-money span and
   announce "currency:update" so the currency converter re-reads it.

   Listening on the form rather than on document is deliberate - the no-pick
   variant <select> dispatches its variant:change without bubbling.
   ------------------------------------------------------------------------- */
import { formatMoney } from '@theme/utilities';

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

if (!customElements.get('hdt-card-offer')) {
  class CardOffer extends HTMLElement {
    constructor() {
      super();
      this._onVariantChanged = this.onVariantChanged.bind(this);
    }

    get form() {
      return (this._form = this._form || document.forms[this.getAttribute('form')]);
    }

    get percent() {
      const value = parseFloat(this.getAttribute('percent'));
      return Number.isFinite(value) ? Math.min(Math.max(value, 0), 100) : 0;
    }

    get moneyFormat() {
      const settings = (window.themeHDN && window.themeHDN.settings) || {};
      return settings.currencyFormat || settings.moneyFormat || '';
    }

    connectedCallback() {
      this.form?.addEventListener('variant:change', this._onVariantChanged);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('variant:change', this._onVariantChanged);
    }

    onVariantChanged(event) {
      const variant = event.detail && event.detail.variant;
      // No variant selected (no-pick mode): leave the rendered price alone.
      if (!variant || variant.price == null) return;
      this.render(Number(variant.price) || 0);
    }

    /* money_format carries <span class=money> markup, so every amount is
       written with innerHTML inside a .hdt-money wrapper - the same shape
       Liquid renders, which is what the currency converter re-reads. */
    money(cents) {
      return `<span class="hdt-money">${formatMoney(Math.max(0, Math.round(cents)), this.moneyFormat)}</span>`;
    }

    /* Merchant copy goes in as text, so escape it before it meets the money
       markup in an innerHTML write. */
    static escape(text) {
      return String(text).replace(/[&<>"']/g, (char) => ESCAPES[char]);
    }

    /* price x (100 - percent) / 100, rounded - mirrors the Liquid sum in
       snippets/product-card-offer.liquid so the first paint and every later
       repaint agree. */
    render(price) {
      const target = this.querySelector('[data-card-offer-price]');
      const saving = this.querySelector('[data-card-offer-saving]');
      if (!target && !saving) return;

      const discounted = Math.max(0, Math.round((price * (100 - this.percent)) / 100));
      if (target) target.innerHTML = this.money(discounted);

      if (saving) {
        const template = this.dataset.savingText || '';
        const amount = Math.max(0, price - discounted);
        // Nothing saved on this variant (free, or a 0% offer): drop the tag
        // rather than show "Save 0.00".
        saving.hidden = amount <= 0 || !template;
        saving.innerHTML = saving.hidden
          ? ''
          : template.split('[amount]').map(CardOffer.escape).join(this.money(amount));
      }

      document.dispatchEvent(new CustomEvent('currency:update'));
    }
  }

  customElements.define('hdt-card-offer', CardOffer);
}

/* ---------------------------------------------------------------------------
   Copy button on the product-page offer strip.

   Delegated from the document so it survives the section re-rendering on a
   variant change, and so one listener covers however many strips a page has.

   navigator.clipboard is absent in non-secure contexts and rejects without a
   gesture in some in-app browsers, so every failure falls through to the
   execCommand path. If both fail we select the code instead and say so -
   a false "Copied" is worse than none, because nothing gets pasted.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var RESET_AFTER = 2000;

  function legacyCopy(text) {
    var area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    area.style.opacity = '0';
    document.body.appendChild(area);
    var ok = false;
    try {
      area.select();
      area.setSelectionRange(0, text.length);
      ok = document.execCommand('copy');
    } catch (e) {
      ok = false;
    }
    document.body.removeChild(area);
    return ok;
  }

  function selectNode(node) {
    if (!node) return;
    try {
      var range = document.createRange();
      range.selectNodeContents(node);
      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (e) {
      /* a nicety; failing it changes nothing */
    }
  }

  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest('[data-card-offer-copy]');
    if (!button) return;

    var wrap = button.closest('.hdt-card-offer__coupon');
    if (!wrap) return;

    var codeEl = wrap.querySelector('[data-card-offer-code]');
    var statusEl = wrap.querySelector('[data-card-offer-status]');
    var code = codeEl ? codeEl.textContent.trim() : '';
    if (!code) return;

    function say(message) {
      if (statusEl) statusEl.textContent = message || '';
    }

    function done() {
      wrap.classList.add('is--copied');
      say(button.dataset.msgCopied || 'Copied');
      setTimeout(function () {
        wrap.classList.remove('is--copied');
        say('');
      }, RESET_AFTER);
    }

    function failed() {
      selectNode(codeEl);
      say(button.dataset.msgError || '');
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(function () {
        if (legacyCopy(code)) done();
        else failed();
      });
      return;
    }

    if (legacyCopy(code)) done();
    else failed();
  });
})();
